/* eslint-disable no-console */
import * as React from "react";
import {
  Container,
  Typography,
  Button,
  InputAdornment,
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
  DialogActions,
  DialogContent,
  Checkbox,
  ListItem,
  ListItemIcon,
  Grid,
  ListItemText,
  Hidden,
  ButtonBase,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import BaseFilter from "../../core/models/BaseFilter";
import MaterialSelect from "../MaterialSelect";
import TableViewIcon from "@material-ui/icons/ViewStream";
import CardViewIcon from "@material-ui/icons/ViewModule";
import CloseAllIcon from "@material-ui/icons/UnfoldLess";
import ExpandAllIcon from "@material-ui/icons/UnfoldMore";
import BuildIcon from "@material-ui/icons/Build";
import clsx from "clsx";
import { HomePageViewType } from "../../features/Home";
import SimpleTextField from "../SimpleTextField";
import SimpleDialog from "../SimpleDialog";
import { HomeTableColumns, HomeTableColumnsType } from "../HomeTable";
import FilterIcon from "../../assets/icons/FilterIcon";
import { isSafari } from "../../helpers/deviceDetect";
import { useHubXSubscribe } from "../../HubX";
import { EVENT_HOME_INSTALLATIONS_ALREADY_LOADED } from "../../Events";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing("6.5px", 4),
      display: "flex",
      alignItems: "center",
      background: theme.customPaper.background.cardBackgroundLight,
      divShadow: "0px 2px 3px #0000000D",
      border: `1px solid ${theme.customPaper.background.cardBackgroundLightVariantDark}`,
      borderRadius: 1,
      [theme.breakpoints.only("sm")]: {
        padding: theme.spacing(1.25, 2),
      },

      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.5),
      },
    },

    categoryText: {
      fontSize: "1rem",
      fontWeight: "bold",
      color: theme.customPalette.text.darkgrey,
    },

    menuWrapper: {
      "borderLeft": "1px dashed #707070",
      "flex": 1,
      "display": "flex",
      "justifyContent": "center",
      "alignItems": "center",
      "paddingLeft": theme.spacing(1),
      "marginLeft": theme.spacing(1),

      "&.disable-border": {
        borderLeft: "unset",
      },

      [theme.breakpoints.down("sm")]: {
        borderLeft: "unset",
      },
    },

    mobileDialog: {
      "display": "flex",
      "justifyContent": "center",
      "alignItems": "center",
      "padding": theme.spacing(1),

      "& > *": {
        flexDirection: "column",
        rowGap: `${theme.spacing(3)}px`,
      },
    },

    searchFilter: {
      flex: 1.5,
    },

    button: {
      "display": "flex",
      "flex": 1,
      "alignItems": "center",
      "fontSize": "0.8rem",
      "color": theme.palette.text.secondary,

      "& svg": {
        fontSize: "1.5em",
        width: "1.5em",
        height: "1.5em",
      },
    },

    svg: {
      color: theme.customPalette.text.darkgrey,
      fontSize: "2.5rem",
    },

    svgLight: {
      color: "rgba(0,0,0,0.3)",
    },

    buildIconSvg: {
      paddingRight: theme.spacing(1),
      color: theme.palette.text.secondary,
    },

    selected: {
      color: theme.palette.primary.main,
    },

    dialog: {
      "padding": theme.spacing(2),
      "color": theme.customPalette.text.primary,

      "& h3": {
        fontWeight: "bold",
        margin: theme.spacing(0, 1),
      },

      "& .grid-root": {
        "color": theme.customPalette.text.primary,
        "fontWeight": "bold",

        "& >*:nth-child(6n + 1),>*:nth-child(6n + 2),>*:nth-child(6n + 3)": {
          backgroundColor: theme.customPaper.background.cardBackgroundLight,
        },

        "& >*:nth-child(6n + 4),>*:nth-child(6n + 5),>*:nth-child(6n + 6)": {
          backgroundColor: theme.customPaper.background.cardBackgroundLightContrast,
        },
      },
    },

    hidden: {
      display: "none",
    },

    filterItem: {
      marginBottom: theme.spacing(2),
      "&:nth-child(2)": {
        marginTop: theme.spacing(2),
      },
    },
  })
);

export type HomePageFilterComponentProps = {
  handleChangeSearch?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleToggleView?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleClickExpandAll?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleClickCloseAll?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  filters: BaseFilter[];
  textFieldValue?: string;
  selectedView: HomePageViewType;
  tableDialogOpen: boolean;
  handleCloseDialog: () => void;
  handleOpenDialog: () => void;
  columnsSelected: HomeTableColumnsType[];
  onClickColumnOption: (column: HomeTableColumnsType) => void;
  mobileDialogOpen: boolean;
  handleCloseMobileDialog: () => void;
  handleOpenMobileDialog: () => void;
};

function HomePageFilterComponent(props: HomePageFilterComponentProps) {
  const {
    handleChangeSearch,
    handleToggleView,
    handleClickExpandAll,
    handleClickCloseAll,
    selectedView,
    textFieldValue,
    filters,
    handleOpenDialog,
    mobileDialogOpen,
    handleOpenMobileDialog,
    handleCloseMobileDialog,
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMobileSize = useMediaQuery(theme.breakpoints.down("xs"));

  const [loadingInstallations, setLoadingInstallations] = React.useState<boolean>(false)

  useHubXSubscribe(EVENT_HOME_INSTALLATIONS_ALREADY_LOADED, (isLoading = false) => {
    setLoadingInstallations(isLoading);
  });

  return (
    <Container className={classes.root} maxWidth="xl">
      {/* Pesquisa por texto */}
      <div className={classes.searchFilter}>
        <SimpleTextField
          value={textFieldValue}
          onChange={handleChangeSearch}
          label={!isMobileSize ? "Buscar" : ""}
          placeholder={isMobileSize ? "Buscar" : ""}
          id="filterSearch"
          variant="outlined"
          disabled={loadingInstallations}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Hidden smDown>
        {/* Filtros de card */}
        <div className={clsx(classes.menuWrapper)}>
          <Typography className={classes.categoryText}>Filtros: </Typography>
          {filters.map((filter) => (
            <MaterialSelect key={filter.id} filter={filter} isDisabled={loadingInstallations} hasCheckbox />
          ))}
        </div>

        {/* Botão Toggle View */}
        <div className={classes.menuWrapper}>
          <Tooltip
            title={`Clique para mudar a visualização para estilo de ${
              selectedView === "card" ? "Tabela" : "Card"
            }`}
          >
            <Button disabled={loadingInstallations} onClick={(e) => (handleToggleView ? handleToggleView(e) : console.log(e))}>
              <Typography className={classes.categoryText}>Visualização:</Typography>

              <TableViewIcon
                className={clsx(
                  classes.svg,
                  classes.svgLight,
                  selectedView === "table" && classes.selected
                )}
              />
              <CardViewIcon
                className={clsx(
                  classes.svg,
                  classes.svgLight,
                  selectedView === "card" && classes.selected
                )}
              />
            </Button>
          </Tooltip>
        </div>

        {/* Botão expandir todos os cards/ rows */}
        <div className={classes.menuWrapper}>
          <Button
          disabled={loadingInstallations}
            className={classes.button}
            onClick={(e) => (handleClickExpandAll ? handleClickExpandAll(e) : console.log(e))}
          >
            <ExpandAllIcon className={classes.svg} />

            <Typography>Expandir Tudo</Typography>
          </Button>
        </div>

        {/* Botão fechar todos os cards/ rows */}
        <div className={classes.menuWrapper}>
          <Button
          disabled={loadingInstallations}
            className={classes.button}
            onClick={(e) => (handleClickCloseAll ? handleClickCloseAll(e) : console.log(e))}
          >
            <CloseAllIcon className={classes.svg} />

            <Typography>Fechar Tudo</Typography>
          </Button>
        </div>
      </Hidden>

      <Hidden mdUp>
        <ButtonBase onClick={handleOpenMobileDialog}>
          <FilterIcon />
          <Button
          disabled={loadingInstallations}
            disableFocusRipple
            disableTouchRipple
            disableRipple
            variant="text"
            color="secondary"
            component="span"
          >
            Filtros
          </Button>
        </ButtonBase>
      </Hidden>

      <SimpleDialog
        draggable={false}
        id="mobile-home-filter"
        open={mobileDialogOpen}
        handleClose={handleCloseMobileDialog}
        maxWidth="xs"
        title="Filtrar"
      >
        <DialogContent className={classes.mobileDialog}>
          <div className={clsx(classes.menuWrapper)}>
            <Typography className={classes.categoryText}>Filtros: </Typography>
            {filters.map((filter) => (
              <div key={filter.id} className={isSafari ? classes.filterItem : ""}>
                <MaterialSelect filter={filter} isDisabled={loadingInstallations} />
              </div>
            ))}
          </div>
        </DialogContent>

        <DialogActions>
          <Button
          disabled={loadingInstallations}
            variant="contained"
            onClick={(e) => {
              console.log(e);
              handleCloseMobileDialog();
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </SimpleDialog>

      {/* <SimpleDialog
        maxWidth="md"
        open={tableDialogOpen}
        handleClose={handleCloseDialog}
        title="Filtrar"
      >
        <DialogContent className={classes.dialog}>
          <div className={clsx(classes.menuWrapper, "disable-border")}>
            <Typography className={classes.categoryText}>Filtros: </Typography>
            {filters.map((filter) => (
              <MaterialSelect key={filter.id} filter={filter} />
            ))}
          </div>

          <div>
            <Typography component="h3">Visualizar Colunas</Typography>

            <Grid className={"grid-root"} xs={12} container spacing={1}>
              {HomeTableColumns.map((column, idx) => (
                <Grid xs={4} item key={`table-column-filter-option-${idx}`}>
                  <ListItem>
                    <ListItemIcon>
                      <Checkbox
                        checked={columnsSelected.includes(column)}
                        onClick={() => onClickColumnOption(column)}
                      />
                    </ListItemIcon>
                    <ListItemText>{column}</ListItemText>
                  </ListItem>
                </Grid>
              ))}
            </Grid>
          </div>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleCloseDialog}>
            Ok
          </Button>
        </DialogActions>
      </SimpleDialog> */}
    </Container>
  );
}

export default HomePageFilterComponent;
